import { gallery } from "@webscan/retrieve.js";
import {
  captureStorageIssue,
  clearCaptures,
  deleteCapture,
  loadCaptures,
  type StoredCapture,
} from "../shared/captures";
import { formatPrice, potentialPrice, type PotentialPrice } from "./prices";

interface ColumnarCatalogue {
  dict: { set: Array<[string, string]>; name: string[] };
  printing: {
    set: number[];
    name: number[];
    collector: string[];
    usd?: Array<number | null>;
    usd_foil?: Array<number | null>;
    eur?: Array<number | null>;
    eur_foil?: Array<number | null>;
  };
}

type Finishes = Parameters<typeof potentialPrice>[0];

const unpriced: PotentialPrice = { cents: null, currency: "USD", foil: false, plain: null };

function priceForRow(row: StoredCapture, prices: Map<string, PotentialPrice>): PotentialPrice {
  // A chosen online printing can differ in language/finish from another history row
  // with the same legacy set/collector/name key, and may be absent from the index.
  return row.verdict === "RESOLVED" && row.chosenPrinting
    ? potentialPrice(row.chosenPrinting)
    : (prices.get(row.key) ?? unpriced);
}

function maximum(first: number | null, second: number | null | undefined): number | null {
  if (second == null) return first;
  return first == null ? second : Math.max(first, second);
}

/** Resolve the entire history with one catalogue walk, including shared-key card faces. */
function historyPrices(rows: StoredCapture[]): Map<string, PotentialPrice> {
  const catalogue = gallery.catalogue as ColumnarCatalogue | null;
  const prices = new Map<string, PotentialPrice>();
  if (!catalogue?.printing || !catalogue.dict || !rows.length) return prices;
  const keys = new Set(rows.map((row) => row.key));
  const collectors = new Set(rows.map((row) => row.collector));
  const finishes = new Map<string, Finishes>();
  const columns = catalogue.printing;
  for (let i = 0; i < columns.collector.length; i++) {
    const collector = columns.collector[i];
    if (!collectors.has(collector)) continue;
    const setCode = catalogue.dict.set[columns.set[i]]?.[0];
    const name = catalogue.dict.name[columns.name[i]];
    const key = `${setCode}/${collector}/${name}`;
    if (!keys.has(key)) continue;
    const entry = finishes.get(key) ?? { usd: null, usdFoil: null, eur: null, eurFoil: null };
    entry.usd = maximum(entry.usd, columns.usd?.[i]);
    entry.usdFoil = maximum(entry.usdFoil, columns.usd_foil?.[i]);
    entry.eur = maximum(entry.eur, columns.eur?.[i]);
    entry.eurFoil = maximum(entry.eurFoil, columns.eur_foil?.[i]);
    finishes.set(key, entry);
  }
  for (const [key, finishesForPrinting] of finishes) {
    prices.set(key, potentialPrice(finishesForPrinting));
  }
  return prices;
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function button(label: string, className = "button button--ghost"): HTMLButtonElement {
  const node = element("button", className, label);
  node.type = "button";
  return node;
}

function searchText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase();
}

function csvCell(value: string): string {
  // Quoting protects commas; the prefix also keeps a card name from becoming a formula.
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function mountScanHistory(
  root: HTMLElement,
  openCapture: (blob: Blob, title: string) => void,
): { open: () => Promise<void>; close: () => void; refresh: () => Promise<void> } {
  const summary = element("div", "history-summary");
  const count = element("p", "history-count");
  const valueLabel = element("span", "history-meta", "Potential value");
  const total = element("strong", "price", "—");
  const unpricedNote = element("span", "history-meta");
  unpricedNote.hidden = true;
  const totalCopy = element("div", "history-total");
  const totalLabel = element("div", "history-total-label");
  const values = element("details", "history-values");
  const valuesToggle = element("summary", "icon-button history-values-toggle");
  valuesToggle.setAttribute("aria-label", "About values");
  valuesToggle.title = "About values";
  valuesToggle.innerHTML =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7v.01"/></svg>';
  valuesToggle.append(element("span", "sr-only", "About values"));
  const explanation = element(
    "p",
    "history-meta",
    "Higher finish price; foil is not detected. Uncertain printings use the saved match.",
  );
  values.append(valuesToggle, explanation);
  totalLabel.append(valueLabel, values);
  totalCopy.append(totalLabel, total, unpricedNote);
  summary.append(count, totalCopy);
  const search = element("input", "search-field");
  search.type = "search";
  search.placeholder = "Search cards, sets, or numbers";
  search.setAttribute("aria-label", "Search saved scans by card name, set, or collector number");
  search.autocomplete = "off";
  const tools = element("div", "history-tools");
  const segmented = element("div", "segmented");
  segmented.setAttribute("role", "group");
  segmented.setAttribute("aria-label", "Show all scans or unique printings");
  const all = button("All", "");
  const unique = button("Unique", "");
  all.setAttribute("aria-pressed", "true");
  unique.setAttribute("aria-pressed", "false");
  segmented.append(all, unique);
  const actions = element("details", "history-actions");
  const actionsToggle = element("summary", "icon-button history-actions-toggle");
  actionsToggle.setAttribute("aria-label", "Scan actions");
  actionsToggle.title = "Scan actions";
  actionsToggle.innerHTML =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></svg>';
  const actionsMenu = element("div", "history-actions-menu");
  const exportButton = button("Export CSV");
  exportButton.title = "Export the scans shown by the current search and All / Unique filter";
  const clearButton = button("Clear all", "button button--ghost button--danger");
  actionsMenu.append(exportButton, clearButton);
  actions.append(actionsToggle, actionsMenu);
  tools.append(segmented, actions);

  const storage = element("p", "notice");
  storage.hidden = true;
  storage.setAttribute("role", "status");
  const feedback = element("p", "notice");
  feedback.hidden = true;
  feedback.setAttribute("role", "status");
  const confirmation = element("div", "notice");
  confirmation.hidden = true;
  confirmation.setAttribute("role", "group");
  confirmation.setAttribute("aria-label", "Confirm clearing scan history");
  const confirmText = element("p", "", "Delete every saved scan? This cannot be undone.");
  const confirmTools = element("div", "history-tools");
  const cancelClear = button("Keep scans");
  const confirmClear = button("Delete all scans", "button button--danger");
  confirmTools.append(cancelClear, confirmClear);
  confirmation.append(confirmText, confirmTools);
  const list = element("div", "history-list");
  list.setAttribute("aria-label", "Saved scans");
  root.append(summary, search, tools, storage, feedback, confirmation, list);

  let opened = false;
  let revision = 0;
  let busy = false;
  let onlyUnique = false;
  let rows: StoredCapture[] = [];
  let prices = new Map<string, PotentialPrice>();
  const urls = new Set<string>();
  const dayFormat = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timeFormat = new Intl.DateTimeFormat(undefined, {
    dateStyle: "long",
    timeStyle: "medium",
  });

  const revokeImages = (): void => {
    for (const url of urls) URL.revokeObjectURL(url);
    urls.clear();
  };

  const say = (text: string): void => {
    feedback.textContent = text;
    feedback.hidden = !text;
  };

  const visibleRows = (): StoredCapture[] => {
    const words = searchText(search.value).trim().split(/\s+/).filter(Boolean);
    const seen = new Set<string>();
    return rows.filter((row) => {
      const searchable = searchText(`${row.name} ${row.setCode} ${row.collector}`);
      if (!words.every((word) => searchable.includes(word))) return false;
      if (onlyUnique && seen.has(row.key)) return false;
      seen.add(row.key);
      return true;
    });
  };

  const updateControls = (): void => {
    exportButton.disabled = busy || visibleRows().length === 0;
    clearButton.disabled = busy || rows.length === 0;
    confirmClear.disabled = busy;
    cancelClear.disabled = busy;
    for (const remove of list.querySelectorAll<HTMLButtonElement>("[data-delete]")) {
      remove.disabled = busy;
    }
  };

  const render = (): void => {
    if (!opened) return;
    revokeImages();
    list.replaceChildren();
    const visible = visibleRows();
    const totalCount = onlyUnique ? new Set(rows.map((row) => row.key)).size : rows.length;
    const unit = onlyUnique ? "printing" : "scan";
    count.textContent = search.value.trim()
      ? `${visible.length} of ${totalCount} ${unit}${totalCount === 1 ? "" : "s"}`
      : `${visible.length} ${unit}${visible.length === 1 ? "" : "s"}`;
    const totals = new Map<"USD" | "EUR", number>();
    let unpricedCount = 0;
    for (const row of visible) {
      const price = priceForRow(row, prices);
      if (price.cents == null) unpricedCount++;
      else totals.set(price.currency, (totals.get(price.currency) ?? 0) + price.cents);
    }
    total.textContent =
      [...totals].map(([currency, cents]) => formatPrice(cents, currency)).join(" + ") || "—";
    valueLabel.textContent =
      onlyUnique || search.value.trim() ? "Shown potential value" : "Potential value";
    const pricedNote = unpricedCount ? ` ${unpricedCount} without a price.` : "";
    unpricedNote.hidden = !visible.length || !unpricedCount;
    unpricedNote.textContent = `${unpricedCount} unpriced`;
    total.title = `${visible.length} shown. USD and EUR are totaled separately.${pricedNote}`;
    explanation.textContent =
      "Higher finish price; foil is not detected. Uncertain printings use the saved match." +
      (!gallery.catalogue ? " Prices will appear when the catalogue finishes loading." : "");
    const issue = captureStorageIssue();
    storage.hidden = !issue;
    storage.textContent = issue
      ? `Scans that cannot be saved are kept until you close this tab. ${issue}. Export CSV to keep their details.`
      : "";
    all.setAttribute("aria-pressed", String(!onlyUnique));
    unique.setAttribute("aria-pressed", String(onlyUnique));

    if (!visible.length) {
      const empty = element("div", "empty-state");
      empty.append(
        element("strong", "", rows.length ? "No matching scans" : "No scans yet"),
        element("p", "", rows.length ? "Try a different search." : "Scan a card to save it here."),
      );
      list.append(empty);
      updateControls();
      return;
    }

    const quantities = new Map<string, number>();
    for (const row of rows) quantities.set(row.key, (quantities.get(row.key) ?? 0) + 1);
    let previousDay = "";
    const fragment = document.createDocumentFragment();
    for (const row of visible) {
      const date = new Date(row.at);
      const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      if (dayKey !== previousDay) {
        fragment.append(element("h3", "day-label", dayFormat.format(date)));
        previousDay = dayKey;
      }
      const item = element("article", "history-row");
      const title = `${row.name} · ${row.setCode.toUpperCase()} ${row.collector}`;
      item.setAttribute("aria-label", title);
      const open = button("", "history-open");
      const image = element("img", "history-thumb");
      const url = URL.createObjectURL(row.blob);
      urls.add(url);
      image.src = url;
      image.alt = "";
      image.width = 44;
      image.height = 61;
      image.loading = "lazy";
      image.decoding = "async";
      open.addEventListener("click", () => openCapture(row.blob, title));
      const copy = element("span", "history-copy");
      const name = element("span", "history-name", row.name);
      if (!/\s/.test(row.name)) name.classList.add("card-name--single");
      const details = element("span", "history-details");
      const identity = element(
        "span",
        "history-meta history-identity",
        `${row.setCode.toUpperCase()} #${row.collector}`,
      );
      const byPrice = row.verdict === "RESOLVED" && row.resolvedBy === "price";
      const verdictLabel =
        row.verdict === "RESOLVED"
          ? byPrice
            ? "by price"
            : "yours"
          : row.verdict === "ACCEPTED"
            ? "Accepted"
            : "Printing uncertain";
      const verdict = element("span", "verdict", verdictLabel);
      verdict.dataset.verdict = row.verdict.toLowerCase();
      if (byPrice) verdict.dataset.resolvedBy = "price";
      if (row.verdict === "AMBIGUOUS") verdict.title = "The printing is uncertain";
      else if (byPrice)
        verdict.title =
          "Settled by price: the top printings tied on match and were worth about the same, " +
          "so the more valuable one was taken";
      else if (row.verdict === "RESOLVED") verdict.title = "You chose this printing";
      if (row.verdict === "RESOLVED") identity.append(" · ", verdict);
      details.append(identity);
      if (row.verdict !== "RESOLVED") details.append(verdict);
      const copies = quantities.get(row.key) ?? 1;
      const metadata = element("span", "history-context");
      const time = element("time", "sr-only", `Scanned ${timeFormat.format(date)}`);
      time.id = `historyScanTime${row.id}`;
      time.dateTime = date.toISOString();
      open.title = time.textContent ?? "";
      open.setAttribute("aria-describedby", time.id);
      if (row.id < 0) metadata.append(element("span", "history-meta", "This tab only"));
      if (onlyUnique && copies > 1) {
        const quantity = element("span", "history-meta", `${copies} scans`);
        quantity.title = "The latest scan of this printing is shown";
        metadata.append(quantity);
      }
      copy.append(name, details);
      if (metadata.childElementCount) copy.append(metadata);
      const price = priceForRow(row, prices);
      const priceCopy = element("span", "history-price");
      const amount = element("strong", "price", formatPrice(price.cents, price.currency));
      priceCopy.append(amount);
      if (price.foil) priceCopy.append(element("span", "history-meta", "foil potential"));
      if (price.plain != null && price.plain !== price.cents) {
        priceCopy.append(
          element("span", "history-meta", `${formatPrice(price.plain, price.currency)} non-foil`),
        );
      }
      const priceLabel =
        price.cents == null
          ? "Price unavailable"
          : `${amount.textContent}${price.foil ? " foil potential" : " potential value"}`;
      const plainLabel =
        price.plain != null && price.plain !== price.cents
          ? ` ${formatPrice(price.plain, price.currency)} non-foil.`
          : "";
      open.setAttribute(
        "aria-label",
        `View saved image of ${title}. ${verdictLabel}. ${priceLabel}.${plainLabel}`,
      );
      const remove = button("", "icon-button history-delete");
      remove.dataset.delete = String(row.id);
      remove.setAttribute("aria-label", `Delete this scan of ${title}`);
      remove.title = "Delete scan";
      remove.innerHTML =
        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v5M14 11v5"/></svg>';
      remove.addEventListener("click", () => {
        const index = visible.indexOf(row);
        void mutate(
          async () => {
            await deleteCapture(row.id);
            await refresh();
            say(
              rows.some((stored) => stored.id === row.id)
                ? "This scan could not be deleted. Try again."
                : "Scan deleted.",
            );
          },
          () => {
            const remaining = list.querySelectorAll<HTMLButtonElement>(".history-open");
            return remaining[Math.min(index, remaining.length - 1)] ?? actionsToggle;
          },
        );
      });
      open.append(image, copy, priceCopy, time);
      item.append(open, remove);
      fragment.append(item);
    }
    list.append(fragment);
    updateControls();
  };

  const refresh = async (): Promise<void> => {
    if (!opened) return;
    const request = ++revision;
    list.setAttribute("aria-busy", "true");
    try {
      const loaded = await loadCaptures();
      if (!opened || request !== revision) return;
      rows = loaded.sort((first, second) => second.at - first.at);
      prices = historyPrices(rows);
      render();
    } catch (error) {
      if (!opened || request !== revision) return;
      say(
        `Could not load scan history. ${error instanceof Error ? error.message : "Try opening Scans again."}`,
      );
    } finally {
      if (request === revision) list.setAttribute("aria-busy", "false");
    }
  };

  const mutate = async (
    operation: () => Promise<void>,
    focusAfter: () => HTMLElement = () => actionsToggle,
  ): Promise<void> => {
    if (busy) return;
    busy = true;
    updateControls();
    try {
      await operation();
    } catch (error) {
      say(
        `Could not update scan history. ${error instanceof Error ? error.message : "Please try again."}`,
      );
    } finally {
      busy = false;
      updateControls();
      // Keep keyboard navigation in the list without opening the phone's search keyboard.
      if (opened) focusAfter().focus({ preventScroll: true });
    }
  };

  search.addEventListener("input", render);
  all.addEventListener("click", () => {
    onlyUnique = false;
    render();
  });
  unique.addEventListener("click", () => {
    onlyUnique = true;
    render();
  });
  clearButton.addEventListener("click", () => {
    actions.open = false;
    confirmation.hidden = false;
    confirmText.textContent = `Delete all ${rows.length} saved scans, including those hidden by filters? This cannot be undone.`;
    cancelClear.focus();
  });
  cancelClear.addEventListener("click", () => {
    confirmation.hidden = true;
    actionsToggle.focus();
  });
  confirmClear.addEventListener("click", () => {
    void mutate(async () => {
      await clearCaptures();
      confirmation.hidden = true;
      await refresh();
      say(
        rows.length
          ? "Some scans could not be deleted. Please try again."
          : "Scan history cleared.",
      );
    });
  });
  exportButton.addEventListener("click", () => {
    actions.open = false;
    const visible = visibleRows();
    const csv = [
      [
        "Scanned at",
        "Name",
        "Set",
        "Collector number",
        "Verdict",
        "Resolved by",
        "Potential price",
        "Currency",
        "Potential finish",
        "Nonfoil price",
        "Storage",
      ],
      ...visible.map((row) => {
        const price = priceForRow(row, prices);
        return [
          new Date(row.at).toISOString(),
          row.name,
          row.setCode.toUpperCase(),
          row.collector,
          row.verdict,
          row.verdict === "RESOLVED" ? (row.resolvedBy === "price" ? "price" : "you") : "",
          price.cents == null ? "" : (price.cents / 100).toFixed(2),
          price.cents == null ? "" : price.currency,
          price.cents == null ? "" : price.foil ? "foil" : "plain",
          price.plain == null ? "" : (price.plain / 100).toFixed(2),
          row.id < 0 ? "this tab only" : "saved",
        ];
      }),
    ]
      .map((row) => row.map(csvCell).join(","))
      .join("\r\n");
    const url = URL.createObjectURL(new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }));
    const download = document.createElement("a");
    download.href = url;
    download.download = `webscan-scans-${new Date().toISOString().slice(0, 10)}.csv`;
    download.hidden = true;
    document.body.append(download);
    download.click();
    download.remove();
    // Revoking during the click cancels the download on some mobile browsers.
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    say(`Exported ${visible.length} ${visible.length === 1 ? "scan" : "scans"}.`);
    actionsToggle.focus({ preventScroll: true });
  });
  document.addEventListener("pointerdown", (event) => {
    if (!opened || !(event.target instanceof Node)) return;
    if (!actions.contains(event.target)) actions.open = false;
    if (!values.contains(event.target)) values.open = false;
  });
  root.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || (!actions.open && !values.open)) return;
    event.preventDefault();
    event.stopPropagation();
    if (actions.open) {
      actions.open = false;
      actionsToggle.focus();
    } else {
      values.open = false;
      valuesToggle.focus();
    }
  });

  return {
    async open() {
      opened = true;
      say("");
      actions.open = false;
      values.open = false;
      confirmation.hidden = true;
      count.textContent = "";
      unpricedNote.hidden = true;
      list.replaceChildren(element("p", "empty-state", "Loading saved scans…"));
      updateControls();
      await refresh();
    },
    close() {
      opened = false;
      revision++;
      actions.open = false;
      values.open = false;
      confirmation.hidden = true;
      revokeImages();
      list.replaceChildren();
      list.setAttribute("aria-busy", "false");
      rows = [];
      prices.clear();
    },
    refresh,
  };
}
